import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { UserNotificationPreferenceUpdateStore } from "../../store";
import { NotificationType } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { EmailFrequency } from "../../../../api/graphql";
import { FormControl } from "@mui/material";

export type UserNotificationPreferenceUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  notificationTypeOptions: { id: string; displayId: string }[];
  notificationTypeError?: string;
  notificationType: NotificationType;
  onNotificationTypeChange?: (value: NotificationType) => void;
  inAppEnabled: boolean;
  inAppEnabledSelectedValue?: { displayId: string; id: string } | null;
  onInAppEnabledChange?: (value: boolean) => void;
  inAppEnabledError?: string;
  emailEnabled: boolean;
  emailEnabledSelectedValue?: { displayId: string; id: string } | null;
  onEmailEnabledChange?: (value: boolean) => void;
  emailEnabledError?: string;
  emailFrequencyOptions: { id: string; displayId: string }[];
  emailFrequencyError?: string;
  emailFrequency: EmailFrequency;
  onEmailFrequencyChange?: (value: EmailFrequency) => void;
  userNotificationPreferenceUpdateStore: UserNotificationPreferenceUpdateStore;
};

export const UserNotificationPreferenceUpdateForm: React.FC<UserNotificationPreferenceUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      notificationTypeOptions = [],
      notificationTypeError,
      notificationType = "" as NotificationType,
      onNotificationTypeChange = () => {},
      inAppEnabled = false,
      inAppEnabledError,
      onInAppEnabledChange = () => {},
      emailEnabled = false,
      emailEnabledError,
      onEmailEnabledChange = () => {},
      emailFrequencyOptions = [],
      emailFrequencyError,
      emailFrequency = "" as EmailFrequency,
      onEmailFrequencyChange = () => {},
      userNotificationPreferenceUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="UserNotificationPreference"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "notificationType":
                    onNotificationTypeChange?.(valor);
                    break;
                  case "inAppEnabled":
                    onInAppEnabledChange?.(valor);
                    break;
                  case "emailEnabled":
                    onEmailEnabledChange?.(valor);
                    break;
                  case "emailFrequency":
                    onEmailFrequencyChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={userNotificationPreferenceUpdateStore.getCardErrors(
                "null"
              )}
              title={langStore.groupUserNotificationPreferenceLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="notificationType"
                  autoFocus
                  disabled={false}
                  error={Boolean(notificationTypeError)}
                  helperText={
                    notificationTypeError ||
                    userNotificationPreferenceUpdateStore.warnings[
                      "notificationType"
                    ]?.message
                  }
                  label={
                    langStore.UserNotificationPreferenceNotificationTypeLabel
                  }
                  options={notificationTypeOptions}
                  selectedValue={
                    notificationType
                      ? {
                          id: notificationType,
                          displayId:
                            notificationTypeOptions.find(
                              (o: { id: string }) => o.id === notificationType
                            )?.displayId || notificationType,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onNotificationTypeChange(
                      (value?.id as NotificationType) ||
                        ("" as NotificationType)
                    )
                  }
                />
                <InputBoolean
                  value={inAppEnabled}
                  error={Boolean(inAppEnabledError)}
                  helperText={
                    inAppEnabledError ||
                    userNotificationPreferenceUpdateStore.warnings[
                      "inAppEnabled"
                    ]?.message
                  }
                  label={langStore.UserNotificationPreferenceInAppEnabledLabel}
                  description={
                    langStore.UserNotificationPreferenceInAppEnabledDescription
                  }
                  onChange={(value: boolean) => onInAppEnabledChange(value)}
                />
                <InputBoolean
                  value={emailEnabled}
                  error={Boolean(emailEnabledError)}
                  helperText={
                    emailEnabledError ||
                    userNotificationPreferenceUpdateStore.warnings[
                      "emailEnabled"
                    ]?.message
                  }
                  label={langStore.UserNotificationPreferenceEmailEnabledLabel}
                  description={
                    langStore.UserNotificationPreferenceEmailEnabledDescription
                  }
                  onChange={(value: boolean) => onEmailEnabledChange(value)}
                />
                <InputReference
                  id="emailFrequency"
                  disabled={false}
                  error={Boolean(emailFrequencyError)}
                  helperText={
                    emailFrequencyError ||
                    userNotificationPreferenceUpdateStore.warnings[
                      "emailFrequency"
                    ]?.message
                  }
                  label={
                    langStore.UserNotificationPreferenceEmailFrequencyLabel
                  }
                  options={emailFrequencyOptions}
                  selectedValue={
                    emailFrequency
                      ? {
                          id: emailFrequency,
                          displayId:
                            emailFrequencyOptions.find(
                              (o: { id: string }) => o.id === emailFrequency
                            )?.displayId || emailFrequency,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onEmailFrequencyChange(
                      (value?.id as EmailFrequency) || ("" as EmailFrequency)
                    )
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
